import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  clearHelpArticleHeroMedia,
  createHelpArticle,
  deleteHelpArticle,
  deleteHelpArticlePreviewImage,
  getHelpArticle,
  getHelpArticles,
  publishHelpArticle,
  setHelpArticleYouTube,
  unpublishHelpArticle,
  updateHelpArticle,
  uploadHelpArticleHeroImage,
  uploadHelpArticleHeroPdf,
  uploadHelpArticleHeroVideo,
  uploadHelpArticlePreviewImage,
} from '../../../api/helpArticles';
import { useAuth } from '../../auth/AuthProvider';
import type { HelpArticleUpsertPayload } from '../../../types/helpArticles';

export function useHelpArticles(statusFilter?: string) {
  const { tokens } = useAuth();

  return useQuery({
    queryKey: ['help-articles', statusFilter],
    queryFn: () => getHelpArticles(tokens!.access_token, statusFilter),
    enabled: Boolean(tokens?.access_token),
  });
}

export function useHelpArticle(articleId: string | null) {
  const { tokens } = useAuth();

  return useQuery({
    queryKey: ['help-article', articleId],
    queryFn: () => getHelpArticle(articleId!, tokens!.access_token),
    enabled: Boolean(tokens?.access_token && articleId),
  });
}

export function useCreateHelpArticle() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: HelpArticleUpsertPayload) => createHelpArticle(payload, tokens!.access_token),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['help-articles'] }),
  });
}

export function useUpdateHelpArticle() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, payload }: { articleId: string; payload: HelpArticleUpsertPayload }) =>
      updateHelpArticle(articleId, payload, tokens!.access_token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', variables.articleId] });
    },
  });
}

export function useDeleteHelpArticle() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => deleteHelpArticle(articleId, tokens!.access_token),
    onSuccess: (_, articleId) => {
      queryClient.removeQueries({ queryKey: ['help-article', articleId] });
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
    },
  });
}

export function usePublishHelpArticle() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => publishHelpArticle(articleId, tokens!.access_token),
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', articleId] });
    },
  });
}

export function useUnpublishHelpArticle() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => unpublishHelpArticle(articleId, tokens!.access_token),
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', articleId] });
    },
  });
}

export function useUploadHelpArticleHeroImage() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, file }: { articleId: string; file: File }) =>
      uploadHelpArticleHeroImage(articleId, file, tokens!.access_token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', variables.articleId] });
    },
  });
}

export function useUploadHelpArticlePreviewImage() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, file }: { articleId: string; file: File }) =>
      uploadHelpArticlePreviewImage(articleId, file, tokens!.access_token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', variables.articleId] });
    },
  });
}

export function useDeleteHelpArticlePreviewImage() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => deleteHelpArticlePreviewImage(articleId, tokens!.access_token),
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', articleId] });
    },
  });
}

export function useUploadHelpArticleHeroVideo() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, file }: { articleId: string; file: File }) =>
      uploadHelpArticleHeroVideo(articleId, file, tokens!.access_token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', variables.articleId] });
    },
  });
}

export function useUploadHelpArticleHeroPdf() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, file }: { articleId: string; file: File }) =>
      uploadHelpArticleHeroPdf(articleId, file, tokens!.access_token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', variables.articleId] });
    },
  });
}

export function useSetHelpArticleYouTube() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ articleId, youtubeUrl }: { articleId: string; youtubeUrl: string }) =>
      setHelpArticleYouTube(articleId, youtubeUrl, tokens!.access_token),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', variables.articleId] });
    },
  });
}

export function useClearHelpArticleHeroMedia() {
  const { tokens } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (articleId: string) => clearHelpArticleHeroMedia(articleId, tokens!.access_token),
    onSuccess: (_, articleId) => {
      queryClient.invalidateQueries({ queryKey: ['help-articles'] });
      queryClient.invalidateQueries({ queryKey: ['help-article', articleId] });
    },
  });
}
